import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, Clock, MapPin } from 'lucide-react';
import { api } from '../../lib/api';

type CheckinState = 'open' | 'checked_in' | 'closed' | 'too_early' | 'too_late';

type CheckinInfo = {
  start_time: string;
  timezone: string;
  status: string;
  checked_in_at: string | null;
  bookee_name: string;
  brand_name: string;
  staff_name: string;
  location_name: string;
  location_address: string;
  service_name: string;
  meeting_link: string | null;
  state: CheckinState;
};

function fmt(startUtc: string, tz: string): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
    hour12: true,
  }).format(new Date(startUtc));
}

export default function CheckInPage() {
  const { token } = useParams();
  const [info, setInfo] = useState<CheckinInfo | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) return;
    api<CheckinInfo>(`/book/checkin/${token}`)
      .then(setInfo)
      .catch((e) => setErr((e as Error).message || 'load_failed'));
  }, [token]);

  async function checkIn() {
    if (!token) return;
    setSubmitting(true);
    setErr(null);
    try {
      await api(`/book/checkin/${token}`, { method: 'POST' });
      setDone(true);
    } catch (e) {
      const code = (e as Error).message;
      // If the window closed between load and tap, refresh the info so the
      // page shows the right state rather than a raw error.
      if (code === 'not_checkin_able') {
        api<CheckinInfo>(`/book/checkin/${token}`).then(setInfo).catch(() => {});
      }
      setErr(code || 'submit_failed');
    } finally {
      setSubmitting(false);
    }
  }

  if (err === 'not_found') {
    return (
      <div className="card" style={{ maxWidth: 460, margin: '12vh auto', textAlign: 'center' }}>
        <h1>Appointment not found</h1>
        <p className="muted">This check-in link isn&rsquo;t valid. Double-check the link in your reminder.</p>
      </div>
    );
  }
  if (!info && !err) return <p className="muted">Loading…</p>;
  if (!info) return <p className="err">Error: {err}</p>;

  const card = (children: React.ReactNode) => (
    <div className="card" style={{ maxWidth: 460, margin: '10vh auto', textAlign: 'center' }}>
      {children}
    </div>
  );

  // Post-check-in success (or already checked in).
  if (done || info.state === 'checked_in') {
    return card(
      <>
        <CheckCircle2 size={40} color="#16a34a" style={{ margin: '0 auto 12px' }} />
        <h1>You&rsquo;re checked in</h1>
        <p>
          Thanks, {info.bookee_name.split(' ')[0]} — <strong>{info.staff_name}</strong> has been
          notified and will be with you shortly.
        </p>
        <p className="muted" style={{ marginTop: '1rem' }}>
          {info.service_name} · {fmt(info.start_time, info.timezone)}
        </p>
      </>,
    );
  }

  if (info.state === 'too_early') {
    return card(
      <>
        <Clock size={36} style={{ margin: '0 auto 12px', color: '#888' }} />
        <h1>Almost time</h1>
        <p>
          Check-in opens closer to your appointment. Come back when you arrive for your{' '}
          <strong>{info.service_name}</strong>.
        </p>
        <p className="muted" style={{ marginTop: '1rem' }}>{fmt(info.start_time, info.timezone)}</p>
      </>,
    );
  }

  if (info.state === 'closed' || info.state === 'too_late') {
    return card(
      <>
        <h1>Check-in unavailable</h1>
        <p className="muted">
          {info.status === 'cancelled'
            ? 'This appointment was cancelled.'
            : 'This appointment&rsquo;s check-in window has closed. Please speak to the front desk.'}
        </p>
      </>,
    );
  }

  // state === 'open'
  return card(
    <>
      <h1>Check in</h1>
      <p className="muted" style={{ marginTop: 0 }}>{info.brand_name}</p>
      <div style={{ textAlign: 'left', margin: '1.25rem 0', lineHeight: 1.7 }}>
        <div><strong>{info.service_name}</strong> with {info.staff_name}</div>
        <div className="muted"><Clock size={13} style={{ verticalAlign: '-2px' }} /> {fmt(info.start_time, info.timezone)}</div>
        {info.location_name && (
          <div className="muted">
            <MapPin size={13} style={{ verticalAlign: '-2px' }} /> {info.location_address || info.location_name}
          </div>
        )}
      </div>
      {err && err !== 'not_checkin_able' && <p className="err">Something went wrong — try again.</p>}
      <button className="primary lg" onClick={checkIn} disabled={submitting} style={{ width: '100%' }}>
        {submitting ? 'Checking in…' : "I'm here — check in"}
      </button>
    </>,
  );
}
